import { action, makeObservable, observable } from 'mobx';
import { RouterState, RouterStore, createRouterState } from 'mobx-state-router';
import { RouterPagesStore } from './routerPages';
import { IComponentName } from '../interfaces';
import { equalObjects } from '../instruments';

type Params = Record<string, string>;
type QueryParams = Record<string, any>;

type IRouteSnapshot = {
    name: IComponentName;
    params?: Params;
    queryParams?: QueryParams;
};
type IRouteSnapshotStrong = IRouteSnapshot & {
    name: IComponentName;
};

export class UpdateRouterStore extends RouterStore {
    historyStack: IRouteSnapshot[] = [];
    blackListHistoryRouteNames: string[] = [
        'Initialization',
        'Login',
        'notFound',
        'ResetPassword',
        'SignUp',
        // mirror pagesPublic.map((i) => i.componentName)
    ];
    defaultComponentName = 'Dashboard';

    constructor(
        routes: import('mobx-state-router').Route[],
        notFoundState: RouterState,
        options: {
            initialState?: RouterState;
            queryParseOptions?: any;
            queryStringifyOptions?: any;
        } = {},
    ) {
        super(routes, notFoundState, options);

        makeObservable(this, {
            historyStack: observable,
            pushToHistory: action,
            goBack: action,
            clearHistory: action,
        });
    }
    previousRoute(): IRouteSnapshot | null {
        return this.historyStack.length > 1 ? this.historyStack[this.historyStack.length - 2] : null;
    }
    currentRoute(): IRouteSnapshot {
        return this.historyStack?.[this.historyStack.length - 1] || {};
    }

    isNotBlackListRouteName(name: string): boolean {
        return !this.blackListHistoryRouteNames.includes(name);
    }

    pushToHistory(snapshot: IRouteSnapshot): void {
        const currentRoute = this.currentRoute();
        if (this.isNotBlackListRouteName(snapshot.name)) {
            const isCurrent = snapshot.name === currentRoute.name;
            if (isCurrent) {
                this.historyStack[this.historyStack.length - 1] = {
                    name: snapshot.name,
                    params: { ...currentRoute.params, ...snapshot.params },
                    queryParams: { ...currentRoute.queryParams, ...snapshot.queryParams },
                };
            } else {
                this.historyStack.push(snapshot);
                if (this.historyStack.length > 50) {
                    this.historyStack.shift();
                }
            }
        }
    }

    replace(page: IRouteSnapshotStrong): void {
        if (this.isNotBlackListRouteName(page.name)) {
            if (equalObjects(page, this.previousRoute() || {})) {
                this.historyStack = this.historyStack.slice(0, -1);
            } else if (!equalObjects(page, this.currentRoute())) {
                this.historyStack[this.historyStack.length - 1] = page;
            }
        }
    }

    goBack() {
        const previousRoute = this.previousRoute();
        if (previousRoute) {
            this.goTo(previousRoute.name, {
                params: previousRoute?.params || {},
                queryParams: previousRoute?.queryParams ?? {},
                replaceHistory: false,
            });
        } else {
            this.goTo(this.defaultComponentName, {
                params: {},
                queryParams: {},
                replaceHistory: false,
            });
        }
        this.historyStack.pop();
    }

    clearHistory() {
        this.historyStack = [];
    }
}

const notFound = createRouterState('notFound');

export const AppRouterStore = new UpdateRouterStore(RouterPagesStore.getInstance().allRoutes, notFound);
